import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { getPrograms, getVideos } from "../../lib/api";
import { createVideo, deleteVideo, updateVideo } from "../../lib/adminApi";
import { getAdminErrorMessage } from "../../lib/adminForm";
import { AdminField, AdminModal, AdminTable } from "../../components/admin/AdminUi";
import { MediaInput } from "../../components/admin/MediaInput";
import { VideoSourceInput } from "../../components/admin/VideoSourceInput";

const EMPTY = {
  title: "",
  coverImage: "",
  description: "",
  youtubeUrl: "",
  videoFileUrl: "",
  programId: "",
  duration: "",
  views: "",
  publishedAt: "",
  order: 0,
};

export default function AdminVideos() {
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => getVideos().then(setItems).catch(() => setItems([]));

  useEffect(() => {
    load();
    getPrograms().then(setPrograms).catch(() => setPrograms([]));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item, programId: item.programId || "" });
    setError("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        programId: form.programId || null,
        order: Number(form.order) || 0,
      };
      if (editing) await updateVideo(editing.id, payload);
      else await createVideo(payload);
      setOpen(false);
      try {
        await load();
      } catch {
        // Kayıt başarılı; liste yenileme hatası kayıt hatası gibi gösterilmez.
      }
    } catch (err) {
      setError(getAdminErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu video silinecek.")) return;
    await deleteVideo(id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-5">
        <h2 className="font-display font-bold text-2xl text-white">Program Videoları</h2>
        <button type="button" onClick={openCreate} className="tv-btn-primary !py-2.5 text-sm inline-flex items-center gap-2">
          <Plus size={16} /> Yeni Video
        </button>
      </div>

      <AdminTable columns={["Video", "Program", "Süre", "Sıra", ""]}>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-[var(--tv-border)] last:border-0">
            <td className="px-4 py-3">
              <p className="font-semibold text-white">{item.title}</p>
              <p className="text-xs text-[var(--tv-muted)] mt-0.5 line-clamp-1">{item.description}</p>
            </td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.programName || "—"}</td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.duration}</td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.order}</td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => openEdit(item)} className="tv-btn-ghost !py-1.5 !px-2.5 text-xs">
                  <Pencil size={14} />
                </button>
                <button type="button" onClick={() => remove(item.id)} className="tv-btn-ghost !py-1.5 !px-2.5 text-xs text-red-300">
                  <Trash2 size={14} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>

      <AdminModal open={open} title={editing ? "Video Düzenle" : "Yeni Video"} onClose={() => setOpen(false)}>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
          <AdminField label="Başlık" className="md:col-span-2">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="tv-input" />
          </AdminField>
          <AdminField label="Program">
            <select value={form.programId} onChange={(e) => setForm({ ...form, programId: e.target.value })} className="tv-input">
              <option value="">Program seçin</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </AdminField>
          <AdminField label="Sıra">
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="tv-input" />
          </AdminField>
          <VideoSourceInput
            className="md:col-span-2"
            youtubeUrl={form.youtubeUrl}
            videoFileUrl={form.videoFileUrl}
            onYoutubeChange={(youtubeUrl) => setForm({ ...form, youtubeUrl })}
            onVideoFileChange={(videoFileUrl) => setForm({ ...form, videoFileUrl })}
          />
          <MediaInput
            label="Kapak görseli"
            className="md:col-span-2"
            value={form.coverImage}
            onChange={(coverImage) => setForm({ ...form, coverImage })}
          />
          <AdminField label="Açıklama" className="md:col-span-2">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="tv-input resize-none" />
          </AdminField>
          <AdminField label="Süre">
            <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="tv-input" placeholder="12:30" />
          </AdminField>
          <AdminField label="Görüntülenme">
            <input value={form.views} onChange={(e) => setForm({ ...form, views: e.target.value })} className="tv-input" placeholder="1.2B görüntüleme" />
          </AdminField>
          <AdminField label="Yayın zamanı" className="md:col-span-2">
            <input value={form.publishedAt} onChange={(e) => setForm({ ...form, publishedAt: e.target.value })} className="tv-input" placeholder="1 gün önce" />
          </AdminField>
          {error && <p className="md:col-span-2 text-sm text-red-400">{error}</p>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="tv-btn-ghost">İptal</button>
            <button type="submit" disabled={saving} className="tv-btn-primary">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
